import { useCallback, useEffect, useRef, useState } from 'react';
import TourismExperiencesService from '../services/tourismExperiencesService';
import WalkingToursService from '../services/walkingToursService';
import { normalizeTourDetail } from '../utils/tourCard';

// Module-level, as useTourRoutes: a tour's detail outlives closing and
// reopening its card. After ten minutes it is asked for again the next time
// it opens, the last copy showing meanwhile.
const DETAIL_TTL_MS = 10 * 60 * 1000;
const cache = new Map();    // key -> { at, detail, failed }
const inFlight = new Map(); // key -> Promise<void>
// Bumped by a clear: a read that started before it lands on nothing.
let generation = 0;
// Bumped by a save anywhere (expireTourDetails): a read that started before
// it lands already due, as its `saved` may be from before the save.
let savesSeq = 0;

const keyOf = (tour) => (tour ? `${tour.content_type}:${tour.id}` : null);

const fresh = (key) => {
  const hit = cache.get(key);
  return !!hit && !hit.failed && Date.now() - hit.at < DETAIL_TTL_MS;
};

/**
 * Forget every tour's detail — at an auth boundary (cacheInvalidation's
 * invalidateSessionCaches): the stops are read for the viewer (an author
 * sees their own restricted stop; a hidden author their own words), so a
 * copy must not outlive the account that read it.
 */
export const clearTourDetailCache = () => {
  generation += 1;
  cache.clear();
  inFlight.clear();
};

/**
 * A save or unsave anywhere (cacheInvalidation's invalidateUserContentCaches):
 * every tour's detail is asked for again the next time its card opens, since
 * it says whether the viewer has the tour saved; the last copy shows meanwhile.
 */
export const expireTourDetails = () => {
  savesSeq += 1;
  for (const [key, hit] of cache) cache.set(key, { ...hit, at: 0 });
};

/**
 * What the save sheet just did to a tour's saves, on its cached detail, so
 * the card reopened inside the ten minutes stays lit (or unlit).
 */
export const setTourDetailSaved = (contentType, id, saved) => {
  const hit = cache.get(`${contentType}:${id}`);
  if (hit?.detail) cache.set(`${contentType}:${id}`, { ...hit, detail: { ...hit.detail, saved } });
};

function load(tour) {
  const key = keyOf(tour);
  if (inFlight.has(key)) return inFlight.get(key);
  const gen = generation;
  const seq = savesSeq;
  const read = tour.content_type === 'tourism_experience'
    // extras: the travelers' verdict and the stops' ticket prices.
    ? TourismExperiencesService.getExperience(tour.id, { extras: true })
    : WalkingToursService.getTour(tour.id);
  const p = read
    .then(res => {
      if (gen !== generation) return;
      const detail = normalizeTourDetail(tour.content_type, res);
      cache.set(key, { at: seq === savesSeq ? Date.now() : 0, detail: detail || cache.get(key)?.detail || null, failed: !detail });
    })
    .catch(err => {
      console.error('Tour detail error:', err);
      if (gen !== generation) return;
      cache.set(key, { at: Date.now(), detail: cache.get(key)?.detail || null, failed: true });
    })
    .finally(() => { if (gen === generation) inFlight.delete(key); });
  inFlight.set(key, p);
  return p;
}

/**
 * The detail behind a Discover tour's card (`utils/tourCard`'s shape):
 * photos, minutes and words per stop, the facts, the travelers' verdict.
 * Returns `detail` (the last copy loaded, or null), `failed` (loading
 * failed and there is nothing to show), `loading`, and `retry`.
 */
export default function useTourDetail(tour) {
  const key = keyOf(tour);
  const [, setVersion] = useState(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const ensure = useCallback(() => {
    if (!tour) return;
    const k = keyOf(tour);
    if (fresh(k)) return;
    // Re-render now (the card swaps an error for its spinner), then when
    // the detail lands.
    setVersion(v => v + 1);
    load(tour).then(() => { if (mounted.current) setVersion(v => v + 1); });
  }, [tour]);

  useEffect(() => { ensure(); }, [ensure]);

  const hit = key ? cache.get(key) : null;
  const loading = !!key && inFlight.has(key);
  return {
    detail: hit?.detail || null,
    failed: !!hit?.failed && !hit?.detail && !loading,
    loading,
    retry: ensure
  };
}
